import { useRef } from 'react'
import {
  setEndConfirmTime,
  useAppDispatch,
  useCheckoutState,
  useCheckoutToken,
  useHasSavedPaymentMethods,
  useAppSelector,
  profilingIdSelector,
  selectedPaymentTypeSelector,
  selectedPaymentMethod as selectedPaymentMethodSelector,
  startedCheckoutSelector,
  selectedAutopayEnabledSelector,
  isEnabledPBIAutoPaySelector,
  creditCheckAgreeSelector,
  termsDisclosureAgreeSelector,
  checkoutAccountOpeningDisclosureSelector,
  confirmTermsViewedSelector,
} from '../../../state'
import { checkoutService } from '@afterpaytouch/portal-api/consumer/checkout'
import { useCardOwnerConsumerId, useUserHasApplePaylaterCard } from '../../../state/applePaylater/hooks'
import { useSaveOfferParams, useIssueCardParams, useCloseApplePaylater } from '../hooks'
import { sendPurchaseEvent } from '../../../integrations/googletagmanager/utils'
import { PaymentType, PaymentMethodType } from '@afterpay/types'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { parseCookies } from 'nookies'
import { Theme } from '@afterpaytouch/core'
import { THEME_COOKIE_NAME } from '../../../model'
import { app } from '../../../env'
import { ContinueCheckoutRequest } from '@afterpaytouch/portal-api/types'

interface FinaliseCheckoutOptions {
  shouldUpdatePreferredPaymentDay?: boolean
  onOAuthAuthorization?: () => Promise<void>
  onSuccess?: () => void
  onFailure?: (errorCode?: string) => void
}

export const useFinaliseApplePaylaterCheckout = (): { finaliseCheckout: (options?: FinaliseCheckoutOptions) => Promise<void> } => {
  const dispatch = useAppDispatch()
  const { started, checkout, confirmed } = useCheckoutState()
  const token = useCheckoutToken()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const userHasApplePaylaterCard = useUserHasApplePaylaterCard()
  const saveOfferParams = useSaveOfferParams()
  const issueCardParams = useIssueCardParams()
  const { useContinueCheckoutMutation } = checkoutService
  const [continueCheckout] = useContinueCheckoutMutation()
  const close = useCloseApplePaylater()
  const cardOwnerConsumerId = useCardOwnerConsumerId()

  const profilingSessionId = useAppSelector(profilingIdSelector)
  const paymentType = useAppSelector(selectedPaymentTypeSelector)
  const selectedPaymentMethod = useAppSelector(selectedPaymentMethodSelector)
  const startedCheckout = useAppSelector(startedCheckoutSelector)
  const PCLAutopay = useAppSelector(selectedAutopayEnabledSelector)
  const PBIAutopay = useAppSelector(isEnabledPBIAutoPaySelector)
  const creditCheckAgree = useAppSelector(creditCheckAgreeSelector)
  const termsDisclosureAgree = useAppSelector(termsDisclosureAgreeSelector)
  const accountOpeningDisclosureConsent = useAppSelector(checkoutAccountOpeningDisclosureSelector)
  const confirmTermsViewed = useAppSelector(confirmTermsViewedSelector)

  const confirmedRef = useRef(confirmed)
  confirmedRef.current = confirmed

  const finaliseCheckout = async (options: FinaliseCheckoutOptions = {}): Promise<void> => {
    const { shouldUpdatePreferredPaymentDay, onOAuthAuthorization, onSuccess, onFailure } = options

    const currentConsumerUuid = issueCardParams?.consumer.consumerUuid
    if (cardOwnerConsumerId != null && currentConsumerUuid != null && currentConsumerUuid !== '' && cardOwnerConsumerId !== currentConsumerUuid) {
      onFailure?.('CONSUMER_MISMATCH')
      return
    }

    const { APP_VERSION } = app
    const cookies = parseCookies()
    const checkoutTheme = cookies[THEME_COOKIE_NAME] as Theme
    const termsViewed = `${confirmTermsViewed ?? ''}-${APP_VERSION}`

    const autoPayMap: Record<PaymentType, boolean | undefined> = {
      [PaymentType.PBI]: PBIAutopay,
      [PaymentType.PCL]: PCLAutopay,
      [PaymentType.PAD]: undefined,
    }
    const autopay = paymentType ? autoPayMap[paymentType] : PBIAutopay

    const donationCampaign = startedCheckout?.donationCampaign
    const checkoutClient = 'checkout-redesign-v1'

    let paymentMethodType: PaymentMethodType | undefined
    let paymentMethodId = 0

    if (selectedPaymentMethod) {
      paymentMethodType = selectedPaymentMethod.type

      if (selectedPaymentMethod.type === 'ACH_BANK_ACCOUNT' && 'account' in selectedPaymentMethod) {
        paymentMethodId = selectedPaymentMethod.account.id
      } else if (selectedPaymentMethod.type === 'CASH_APP_PAY' && 'cashAppPay' in selectedPaymentMethod) {
        paymentMethodId = selectedPaymentMethod.cashAppPay.id
      } else if (selectedPaymentMethod.type === 'CREDIT_CARD' && 'card' in selectedPaymentMethod) {
        const card = selectedPaymentMethod.card
        if ('id' in card && typeof card.id === 'number') {
          paymentMethodId = card.id
        }
      }
    }

    const continueRequest: ContinueCheckoutRequest = {
      consumer: {
        consumerUuid: issueCardParams?.consumer.consumerUuid ?? '',
      },
      confirmCheckoutParams: {
        paymentMethodType,
        paymentMethodId,
        paymentType,
        ...(profilingSessionId && { profilingSessionId }),
        ...(donationCampaign && { donation: donationCampaign }),
        ...(checkoutClient && { checkoutClient }),
        ...(checkoutTheme && { checkoutTheme }),
        ...{ autopay },
        ...{ creditCheckAgree },
        ...(shouldUpdatePreferredPaymentDay && { shouldUpdatePreferredPaymentDay }),
        ...{ termsDisclosureAgree },
        ...(valueIsNotNullOrUndefined(accountOpeningDisclosureConsent) && {
          accountOpeningDisclosureConsent,
          termsViewed,
        }),
      },
      issueCardParams: {
        userHasApplePaylaterCard: userHasApplePaylaterCard ?? true,
      },
      saveOfferParams:
        saveOfferParams && !saveOfferParams.userWasDeclined
          ? {
              phoneNumber: saveOfferParams.phoneNumber,
              offerDetails: saveOfferParams.offerDetails,
              userCohort: saveOfferParams.userCohort,
              selectedOfferId: saveOfferParams.offerDetails.selectedOfferId,
              selectedOfferLink: saveOfferParams.offerDetails.selectedOfferLink,
            }
          : {
              phoneNumber: null,
              offerDetails: null,
              userCohort: 'AFTERPAY',
              selectedOfferId: '',
              selectedOfferLink: '',
            },
    }

    const continueResponse = await continueCheckout({ token, request: continueRequest }).unwrap()

    await onOAuthAuthorization?.()

    dispatch(setEndConfirmTime(performance.now()))

    const isDeclined = 'userWasDeclined' in continueResponse && continueResponse.userWasDeclined

    if (!isDeclined) {
      const orderAmount = started?.consumerAmount ?? checkout?.amount
      sendPurchaseEvent(token, !hasSavedPaymentMethods, orderAmount)

      onSuccess?.()

      const queryArgsToUse = checkout?.merchant.callbackUrlQueryArgs ?? ''
      await close(continueResponse.merchantRedirectUrl, queryArgsToUse)
    } else {
      const primaryDeclineCode = continueResponse.declineCodes?.[0]
      onFailure?.(primaryDeclineCode)
    }
  }

  return { finaliseCheckout }
}
